import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    ArrowRight, HardDrive, Server, Cloud, Plus, Check, CheckCheck,
    RefreshCw, Wifi, WifiOff, FileCheck2, ShieldCheck,
    Infinity as InfinityIcon, Zap, Search, X,
} from "lucide-react";
import { http, errMsg, formatBytes, formatDuration } from "@/lib/api";
import { toast } from "sonner";
import ConnectStorageModal from "@/components/ConnectStorageModal";
import AdvancedTransferOptions from "@/components/AdvancedTransferOptions";

/**
 * Migrate — single-glance 3-column flow. SOURCE → FLOW → DESTINATION.
 * Replaces the previous 4-step wizard. No thinking. Just click two devices,
 * confirm options, hit Start Migration.
 */

// Passive storage records (no installed software, just a stored
// credential) -- same set StorageEndpointPicker.jsx/Schedules.jsx use to
// exempt these from the role filter below.
const CONNECTION_KINDS = new Set(["nas", "s3", "gcs", "azure"]);

const BANDWIDTH_PRESETS = [
    { v: 0, label: "Unlimited" },
    { v: 10, label: "10 MB/s" },
    { v: 50, label: "50 MB/s" },
    { v: 100, label: "100 MB/s" },
    { v: 500, label: "500 MB/s" },
    { v: 1000, label: "1 GB/s" },
];

export default function MigrateStorage() {
    const navigate = useNavigate();
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);

    const [sourceId, setSourceId] = useState("");
    const [destId, setDestId] = useState("");

    // Options
    const [name, setName] = useState("");
    const [mode, setMode] = useState("one_shot"); // "one_shot" | "continuous"
    const [deltaSync, setDeltaSync] = useState(true);
    const [verifyIntegrity, setVerifyIntegrity] = useState(true);
    const [encryption, setEncryption] = useState(true);
    const [mirrorDeletes, setMirrorDeletes] = useState(false);
    const [retries, setRetries] = useState(3);
    const [retryBackoffMs, setRetryBackoffMs] = useState(400);
    const [bandwidth, setBandwidth] = useState(0);

    const [sourcePath, setSourcePath] = useState("");
    const [destPath, setDestPath] = useState("");

    // Storage modal (launched from either side)
    const [showStorage, setShowStorage] = useState(null); // null | "source" | "destination"

    // Estimate
    const [estimate, setEstimate] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const refreshAgents = async () => {
        try {
            const { data } = await http.get("/agents");
            setAgents(data);
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refreshAgents(); }, []);

    // An installed agent always registers with role="both" -- without
    // including it here, every real agent was excluded from both lists
    // (same bug and same fix as Schedules.jsx's source/destination
    // pickers). CONNECTION_KINDS (nas/s3/gcs/azure) are unconditional on
    // top of that: they're passive storage records, not installed
    // software, so whichever role they happened to be connected under
    // doesn't constrain what they can actually be used for.
    const sources = useMemo(() => agents.filter((a) => CONNECTION_KINDS.has(a.kind) || a.role === "source" || a.role === "both" || !a.role), [agents]);
    const destinations = useMemo(() => agents.filter((a) => CONNECTION_KINDS.has(a.kind) || a.role === "destination" || a.role === "both" || !a.role), [agents]);

    const srcAgent = agents.find((a) => a.id === sourceId) || null;
    const dstAgent = agents.find((a) => a.id === destId) || null;

    // Derive default paths from device config once selected
    useEffect(() => {
        if (!srcAgent) return;
        if (sourcePath) return;
        if (srcAgent.kind === "nas" && srcAgent.nas) {
            setSourcePath(srcAgent.nas.mount_point || "/mnt/moovbytes");
        } else {
            setSourcePath("/data/source");
        }
    }, [srcAgent]);  // eslint-disable-line
    useEffect(() => {
        if (!dstAgent) return;
        if (destPath) return;
        if (dstAgent.kind === "nas" && dstAgent.nas) {
            setDestPath(dstAgent.nas.mount_point || "/mnt/moovbytes");
        } else {
            setDestPath("/data/destination");
        }
    }, [dstAgent]);  // eslint-disable-line

    useEffect(() => {
        if (!name && srcAgent && dstAgent) {
            const d = new Date();
            const stamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
            setName(`${srcAgent.name} → ${dstAgent.name} · ${stamp}`);
        }
    }, [srcAgent, dstAgent]);  // eslint-disable-line

    // Live estimate
    useEffect(() => {
        const run = async () => {
            try {
                const { data } = await http.post("/jobs/estimate", {
                    total_files: 1200,
                    total_bytes: 500 * 1024 * 1024 * 1024,
                    mode,
                });
                setEstimate(data);
            } catch { setEstimate(null); }
        };
        run();
    }, [mode]);

    const validation = (() => {
        if (!sourceId) return { ok: false, msg: "Select a source to continue" };
        if (!destId) return { ok: false, msg: "Select a destination to continue" };
        if (sourceId === destId) return { ok: false, msg: "Source and destination cannot be the same" };
        if (!sourcePath || !destPath) return { ok: false, msg: "Paths are required on both sides" };
        return { ok: true, msg: "Ready to migrate" };
    })();

    const start = async () => {
        if (!validation.ok) return;
        setSubmitting(true);
        try {
            const payload = {
                name: name || "Untitled migration",
                source_agent_id: sourceId,
                dest_agent_id: destId,
                source_path: sourcePath,
                destination_path: destPath,
                migration_type: mode === "continuous" ? "incremental" : "full",
                mode,
                total_files: 1200,
                total_bytes: 500 * 1024 * 1024 * 1024,
                options: {
                    bandwidth_limit_mbps: bandwidth > 0 ? bandwidth : null,
                    retries,
                    retry_backoff_ms: retryBackoffMs,
                    compression: deltaSync,
                    encryption,
                    enable_versioning: verifyIntegrity,
                    mirror_deletes: mirrorDeletes,
                },
            };
            const { data } = await http.post("/jobs", payload);
            toast.success("Migration queued");
            navigate(`/jobs/${data.id}`);
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="relative mx-auto max-w-[1600px] p-6 pb-32">
            {/* Header */}
            <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                        MoovBytes Migrate
                    </div>
                    <h1 className="mt-1 font-display text-4xl font-bold tracking-tight">
                        New Migration
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Connect source → destination. Files flow directly agent-to-agent.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <Link to="/jobs" className="text-sm font-medium text-[#5b5ef0] hover:text-[#4b4ef0]">
                        View all jobs →
                    </Link>
                </div>
            </div>

            <div
                className="mb-6 rounded border border-amber-400/30 bg-amber-500/[0.04] p-3 text-xs text-amber-200/80"
                data-testid="legacy-banner"
            >
                <strong className="text-amber-100">Legacy view.</strong>{" "}
                You're on the block-level rsync wizard. The new{" "}
                <Link to="/migrate/storage" className="font-semibold text-amber-100 underline hover:text-white">
                    NAS-to-NAS engine
                </Link>{" "}
                supports parallel scan, fast-hash dedupe, adaptive workers, and checkpoint resume.
            </div>

            {/* 3-column flow grid (stacks on mobile) */}
            <div className="relative grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(360px,1.2fr)_minmax(0,1fr)]">
                {/* SVG connection pipes (desktop only, behind cards) */}
                <ConnectionPipes active={sourceId && destId} />

                {/* SOURCE */}
                <EndpointPanel
                    kind="source"
                    title="Source"
                    agents={sources}
                    loading={loading}
                    selectedId={sourceId}
                    onSelect={(id) => { setSourceId(id); setSourcePath(""); }}
                    onConnectNew={() => setShowStorage("source")}
                    onRescan={refreshAgents}
                    path={sourcePath}
                    onPathChange={setSourcePath}
                    agent={srcAgent}
                    testIdPrefix="source"
                />

                {/* FLOW */}
                <div className="relative z-10 rounded-2xl border border-border bg-surface p-5">
                    <div className="mb-4 flex items-center justify-between">
                        <div>
                            <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-muted-foreground">
                                Flow
                            </div>
                            <div className="font-display text-xl font-bold">Transfer behavior</div>
                        </div>
                        <FlowIndicator active={validation.ok} />
                    </div>

                    {/* Mode selector */}
                    <Section title="Transfer mode">
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <ModeRadio
                                active={mode === "one_shot"}
                                onClick={() => setMode("one_shot")}
                                title="One-time Migration"
                                sub="Copy everything once, then finish."
                                testId="migrate-mode-oneshot"
                            />
                            <ModeRadio
                                active={mode === "continuous"}
                                onClick={() => setMode("continuous")}
                                title="Continuous Sync"
                                sub="Keep destination continuously in sync."
                                testId="migrate-mode-continuous"
                            />
                        </div>
                    </Section>

                    {/* Options */}
                    <Section title="Options">
                        <div className="space-y-1.5">
                            <OptionToggle
                                icon={Zap}
                                label="Enable Delta Sync"
                                sub="Only transfer changed 4 MB blocks (rsync-style)."
                                checked={deltaSync}
                                onChange={setDeltaSync}
                                testId="migrate-opt-delta"
                            />
                            <OptionToggle
                                icon={FileCheck2}
                                label="Verify Integrity"
                                sub="SHA-256 hash verify every block + whole-file."
                                checked={verifyIntegrity}
                                onChange={setVerifyIntegrity}
                                testId="migrate-opt-integrity"
                            />
                            <OptionToggle
                                icon={ShieldCheck}
                                label="Enable Encryption"
                                sub="AES-256-GCM in-transit."
                                checked={encryption}
                                onChange={setEncryption}
                                testId="migrate-opt-encryption"
                            />
                        </div>
                    </Section>

                    <Section title="Advanced transfer">
                        <AdvancedTransferOptions
                            mirrorDeletes={mirrorDeletes}
                            onMirrorDeletesChange={setMirrorDeletes}
                            retries={retries}
                            onRetriesChange={setRetries}
                            retryBackoffMs={retryBackoffMs}
                            onRetryBackoffMsChange={setRetryBackoffMs}
                            testId="migrate-storage-advanced"
                        />
                    </Section>

                    {/* Bandwidth */}
                    <Section title="Bandwidth limit">
                        <div className="flex items-center gap-2">
                            <div className="relative flex-1">
                                <select
                                    value={bandwidth}
                                    onChange={(e) => setBandwidth(Number(e.target.value))}
                                    data-testid="migrate-bandwidth-select"
                                    className="mb-focus w-full appearance-none rounded-md border border-input bg-background px-3 py-2 pr-8 text-sm"
                                >
                                    {BANDWIDTH_PRESETS.map((b) => (
                                        <option key={b.v} value={b.v}>{b.label}</option>
                                    ))}
                                </select>
                                <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                                    {bandwidth === 0
                                        ? <InfinityIcon size={13} />
                                        : <span className="font-mono text-[10px]">MB/s</span>}
                                </div>
                            </div>
                        </div>
                    </Section>

                    {/* Live summary */}
                    <div className="mt-4 rounded-xl border border-[#5b5ef0]/30 bg-gradient-to-br from-[#5b5ef0]/5 to-[#00C2FF]/5 p-4">
                        <div className="mb-2 flex items-center justify-between">
                            <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-muted-foreground">
                                Live estimate
                            </div>
                            <span className="rounded-sm bg-[#5b5ef0]/15 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-[#5b5ef0]">
                                {mode === "continuous" ? "delta 25%" : "full copy"}
                            </span>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <Stat label="Data" value={estimate ? formatBytes(estimate.effective_bytes) : "—"} />
                            <Stat
                                label="Time"
                                value={estimate ? (formatDuration(estimate.estimated_seconds) || "—") : "—"}
                            />
                            <Stat
                                label="Cost"
                                value={estimate ? `$${estimate.estimated_cost_usd.toFixed(2)}` : "—"}
                            />
                        </div>
                    </div>
                </div>

                {/* DESTINATION */}
                <EndpointPanel
                    kind="destination"
                    title="Destination"
                    agents={destinations}
                    loading={loading}
                    selectedId={destId}
                    onSelect={(id) => { setDestId(id); setDestPath(""); }}
                    onConnectNew={() => setShowStorage("destination")}
                    onRescan={refreshAgents}
                    path={destPath}
                    onPathChange={setDestPath}
                    agent={dstAgent}
                    testIdPrefix="dest"
                />
            </div>

            {/* Name + validation + CTA */}
            <div className="mt-8 space-y-4">
                <div className="rounded-xl border border-border bg-surface p-4">
                    <label className="mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-muted-foreground">
                        Migration name
                    </label>
                    <input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Q2 datacenter migration"
                        data-testid="migrate-name-input"
                        className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    />
                </div>

                <div className="flex flex-col items-center gap-4">
                    <div
                        className={[
                            "rounded-full border px-4 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] transition-all",
                            validation.ok
                                ? "border-[hsl(var(--success))]/50 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]"
                                : "border-[hsl(var(--warning))]/50 bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]",
                        ].join(" ")}
                        data-testid="migrate-validation"
                    >
                        {validation.ok ? "✔ " : "◯ "}{validation.msg}
                    </div>

                    <button
                        type="button"
                        disabled={!validation.ok || submitting}
                        onClick={start}
                        data-testid="migrate-start-btn"
                        className={[
                            "inline-flex items-center gap-2 rounded-full px-8 py-3 text-sm font-semibold uppercase tracking-widest shadow-lg transition-all",
                            validation.ok
                                ? "bg-gradient-to-r from-[#5b5ef0] to-[#00C2FF] text-white shadow-[#5b5ef0]/40 hover:scale-[1.03] hover:shadow-[#5b5ef0]/60"
                                : "cursor-not-allowed bg-muted text-muted-foreground",
                        ].join(" ")}
                    >
                        <CheckCheck size={16} />
                        {submitting ? "Starting…" : "Start Migration"}
                    </button>
                </div>
            </div>

            {/* Connect storage modal — role-scoped so role matches the slot */}
            {showStorage && (
                <ConnectStorageModal
                    role={showStorage}
                    onClose={() => setShowStorage(null)}
                    onCreated={async (a) => {
                        setShowStorage(null);
                        // Show the one-time token briefly
                        toast.success(`${a.name} connected. Token shown on Agents page.`);
                        await refreshAgents();
                        if (a.role === "source") setSourceId(a.id);
                        if (a.role === "destination") setDestId(a.id);
                    }}
                />
            )}
        </div>
    );
}

// =================== Sub-components ===================

function EndpointPanel({
    kind, title, agents, loading, selectedId, onSelect, onConnectNew,
    onRescan, path, onPathChange, agent, testIdPrefix,
}) {
    const [picker, setPicker] = useState(false);
    const [query, setQuery] = useState("");

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return agents;
        return agents.filter((a) =>
            (a.name + " " + (a.hostname || "") + " " + (a.public_address || "")).toLowerCase().includes(q)
        );
    }, [agents, query]);

    const iconFor = (a) => {
        if (a?.kind === "nas") return HardDrive;
        if (a?.kind === "s3" || a?.kind === "gcs") return Cloud;
        return Server;
    };
    const tint = kind === "source" ? "#5b5ef0" : "#00C2FF";

    return (
        <div
            className="relative z-10 rounded-2xl border border-border bg-surface p-5 transition-all hover:border-foreground/30 hover:shadow-xl"
            data-testid={`${testIdPrefix}-panel`}
        >
            <div className="mb-4 flex items-center justify-between">
                <div>
                    <div
                        className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em]"
                        style={{ color: tint }}
                    >
                        {title}
                    </div>
                    <div className="font-display text-xl font-bold">
                        {agent ? "Connected" : "Pick a device"}
                    </div>
                </div>
                {agent && (
                    <button
                        type="button"
                        onClick={() => { onSelect(""); setPicker(false); }}
                        data-testid={`${testIdPrefix}-clear`}
                        className="rounded-md border border-border p-1.5 text-muted-foreground hover:border-foreground/40 hover:text-foreground"
                        aria-label="Change device"
                        title="Change device"
                    >
                        <X size={13} />
                    </button>
                )}
            </div>

            {!agent ? (
                <div className="space-y-3">
                    <button
                        type="button"
                        onClick={() => setPicker((p) => !p)}
                        data-testid={`${testIdPrefix}-select-btn`}
                        className={[
                            "flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-10 text-sm font-semibold uppercase tracking-wider transition-all",
                            picker
                                ? "border-foreground/40 bg-background"
                                : "border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground",
                        ].join(" ")}
                        style={picker ? { borderColor: tint, color: tint } : undefined}
                    >
                        <Plus size={16} /> Select storage
                    </button>

                    {picker && (
                        <div className="space-y-2 rounded-xl border border-border bg-background p-3">
                            <div className="relative">
                                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <input
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search devices…"
                                    data-testid={`${testIdPrefix}-search`}
                                    className="mb-focus w-full rounded-md border border-input bg-surface py-1.5 pl-7 pr-2 text-xs"
                                />
                            </div>
                            <div className="max-h-60 space-y-1 overflow-y-auto">
                                {loading && (
                                    <div className="py-6 text-center text-[11px] text-muted-foreground">
                                        Scanning fleet…
                                    </div>
                                )}
                                {!loading && filtered.length === 0 && (
                                    <div className="py-6 text-center text-[11px] text-muted-foreground">
                                        No {title.toLowerCase()} devices yet.
                                    </div>
                                )}
                                {filtered.map((a) => {
                                    const Icon = iconFor(a);
                                    const online = a.status === "online";
                                    return (
                                        <button
                                            key={a.id}
                                            type="button"
                                            onClick={() => { onSelect(a.id); setPicker(false); }}
                                            data-testid={`${testIdPrefix}-opt-${a.id}`}
                                            className="flex w-full items-center gap-3 rounded-md border border-transparent px-2 py-2 text-left hover:border-foreground/20 hover:bg-foreground/5"
                                        >
                                            <Icon size={14} style={{ color: tint }} />
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate text-sm font-semibold">{a.name}</div>
                                                <div className="truncate font-mono text-[10px] text-muted-foreground">
                                                    {a.public_address || a.hostname}
                                                </div>
                                            </div>
                                            {online
                                                ? <Wifi size={11} className="text-[hsl(var(--success))]" />
                                                : <WifiOff size={11} className="text-[hsl(var(--destructive))]" />}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="flex items-center justify-between border-t border-border pt-2">
                                <button
                                    type="button"
                                    onClick={onRescan}
                                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground hover:text-foreground"
                                    data-testid={`${testIdPrefix}-rescan`}
                                >
                                    <RefreshCw size={11} /> Rescan
                                </button>
                                <button
                                    type="button"
                                    onClick={onConnectNew}
                                    data-testid={`${testIdPrefix}-connect-new`}
                                    className="inline-flex items-center gap-1 rounded-md bg-[#00C2FF]/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-widest text-[#00C2FF] hover:bg-[#00C2FF]/20"
                                >
                                    <Plus size={11} /> Connect NAS
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <DeviceCard
                    agent={agent}
                    tint={tint}
                    path={path}
                    onPathChange={onPathChange}
                    testIdPrefix={testIdPrefix}
                />
            )}
        </div>
    );
}

function DeviceCard({ agent, tint, path, onPathChange, testIdPrefix }) {
    const Icon = agent.kind === "nas" ? HardDrive : (agent.kind === "s3" || agent.kind === "gcs") ? Cloud : Server;
    const online = agent.status === "online";
    const vendor = agent.kind === "nas"
        ? (agent.nas?.vendor_hint || "NAS storage")
        : agent.kind === "s3" ? "Amazon S3"
        : agent.kind === "gcs" ? "Google Cloud Storage"
        : "VM agent";

    return (
        <div className="space-y-3" data-testid={`${testIdPrefix}-card`}>
            <div
                className="flex items-center gap-3 rounded-xl border p-4"
                style={{ borderColor: `${tint}40`, background: `linear-gradient(135deg, ${tint}0D, transparent)` }}
            >
                <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl shadow-sm"
                    style={{ background: `${tint}22`, color: tint }}
                >
                    <Icon size={20} />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="truncate text-base font-semibold" data-testid={`${testIdPrefix}-name`}>
                        {agent.name}
                    </div>
                    <div className="truncate font-mono text-[10px] text-muted-foreground">
                        {vendor} · {agent.public_address || agent.hostname}
                    </div>
                </div>
                <span
                    className={[
                        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest",
                        online
                            ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]"
                            : "bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))]",
                    ].join(" ")}
                >
                    {online ? <Check size={10} /> : <WifiOff size={10} />}
                    {online ? "connected" : "offline"}
                </span>
            </div>
            <label className="block">
                <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    Path
                </span>
                <input
                    value={path}
                    onChange={(e) => onPathChange(e.target.value)}
                    data-testid={`${testIdPrefix}-path-input`}
                    className="mb-focus w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
                />
            </label>
        </div>
    );
}

function ConnectionPipes({ active }) {
    return (
        <svg
            className="pointer-events-none absolute inset-0 hidden h-full w-full lg:block"
            aria-hidden="true"
            data-testid="connection-pipes"
        >
            <defs>
                <linearGradient id="mbPipe" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#5b5ef0" stopOpacity={active ? "0.95" : "0.2"} />
                    <stop offset="50%" stopColor="#7c5cff" stopOpacity={active ? "0.95" : "0.2"} />
                    <stop offset="100%" stopColor="#00C2FF" stopOpacity={active ? "0.95" : "0.2"} />
                </linearGradient>
            </defs>
            {/* Left pipe: source card right edge → flow card left edge */}
            <line
                x1="30%" y1="45%" x2="38%" y2="45%"
                stroke="url(#mbPipe)" strokeWidth="2" strokeDasharray="6 6"
                className={active ? "animate-pulse" : ""}
            />
            <line
                x1="62%" y1="45%" x2="70%" y2="45%"
                stroke="url(#mbPipe)" strokeWidth="2" strokeDasharray="6 6"
                className={active ? "animate-pulse" : ""}
            />
        </svg>
    );
}

function FlowIndicator({ active }) {
    return (
        <div
            className={[
                "flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-widest",
                active
                    ? "border-[hsl(var(--success))]/50 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]"
                    : "border-border text-muted-foreground",
            ].join(" ")}
        >
            <ArrowRight size={11} className={active ? "animate-pulse" : ""} />
            {active ? "Pipeline ready" : "Awaiting endpoints"}
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="mb-4">
            <div className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                {title}
            </div>
            {children}
        </div>
    );
}

function ModeRadio({ active, onClick, title, sub, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={[
                "flex items-start gap-3 rounded-lg border p-3 text-left transition-all",
                active
                    ? "border-[#5b5ef0] bg-[#5b5ef0]/5 shadow-sm"
                    : "border-border hover:border-foreground/30",
            ].join(" ")}
        >
            <div
                className={[
                    "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                    active ? "border-[#5b5ef0]" : "border-muted-foreground/50",
                ].join(" ")}
            >
                {active && <div className="h-2 w-2 rounded-full bg-[#5b5ef0]" />}
            </div>
            <div>
                <div className="text-sm font-semibold">{title}</div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>
            </div>
        </button>
    );
}

function OptionToggle({ icon: Icon, label, sub, checked, onChange, testId, tone }) {
    const toneBorder = tone === "warn"
        ? (checked ? "border-[hsl(var(--warning))]/50 bg-[hsl(var(--warning))]/5" : "")
        : "";
    return (
        <label className={[
            "flex cursor-pointer items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2.5 transition-colors hover:border-foreground/30",
            toneBorder,
        ].join(" ")}>
            <div className="flex min-w-0 items-center gap-2.5">
                <Icon size={14} className={checked ? "text-[#5b5ef0]" : "text-muted-foreground"} />
                <div className="min-w-0">
                    <div className="text-sm font-medium">{label}</div>
                    <div className="truncate text-[10px] text-muted-foreground">{sub}</div>
                </div>
            </div>
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                onClick={() => onChange(!checked)}
                data-testid={testId}
                className={[
                    "relative h-5 w-9 shrink-0 rounded-full transition-colors",
                    checked ? "bg-[#5b5ef0]" : "bg-muted",
                ].join(" ")}
            >
                <span
                    className={[
                        "absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all",
                        checked ? "left-[18px]" : "left-0.5",
                    ].join(" ")}
                />
            </button>
        </label>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="font-mono text-[9px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {label}
            </div>
            <div className="mt-0.5 font-display text-lg font-bold tabular-nums">{value}</div>
        </div>
    );
}
